import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Text, Flex, useToast, Skeleton, Switch, Icon } from '@chakra-ui/react';
import { PiTrophy } from 'react-icons/pi';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import { ShortcutCard } from '../Home/components/ShortcutCard';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { ReactComponent as TimelineIcon } from '@/assets/icons/Timeline.svg';
import { ReactComponent as ShirtIcon } from '@/assets/icons/Shirt.svg';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { ApplicationComponentId } from '@/model/application-component';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import { t } from '@/i18n';

type Link = {
  label: string;
  url: string;
  icon: ReactNode;
};
const links: Array<Link> = [
  {
    label: t('pages.application_history.application_history_timeline'),
    url: '/application/history/timeline',
    icon: <Icon as={TimelineIcon} />,
  },
  {
    label: t('pages.application_history.application_history_anthem'),
    url: '/application/history/anthem',
    icon: <ChakraFeatherIcon name='volume-2' />,
  },
  {
    label: t('pages.application_history.application_history_gallery'),
    url: '/application/history/gallery',
    icon: <ChakraFeatherIcon name='image' />,
  },
  {
    label: t('pages.application_history.application_history_won_title'),
    url: '/application/history/won-title',
    icon: <PiTrophy />,
  },
  {
    label: t('pages.application_history.application_history_uniforms'),
    url: '/application/history/uniform-gallery',
    icon: <Icon as={ShirtIcon} />,
  },
];

export const ApplicationHistory = () => {
  const toast = useToast();
  const { t } = useTranslation();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(
    undefined,
    ApplicationComponentId.HISTORY,
  );
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_history.application_history_enable_success')
      : t('pages.application_history.application_history_disable_success');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_history.application_history_enable_error')
      : t('pages.application_history.application_history_disable_error');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation(
      [
        { component_id: ApplicationComponentId.TIMELINE, active: pendingAppAreaState },
        { component_id: ApplicationComponentId.ANTHEM, active: pendingAppAreaState },
        { component_id: ApplicationComponentId.GALLERY, active: pendingAppAreaState },
        { component_id: ApplicationComponentId.WON_TITLE, active: pendingAppAreaState },
        { component_id: ApplicationComponentId.UNIFORMS, active: pendingAppAreaState },
      ],
      {
        onSuccess: () => {
          toast({
            status: 'success',
            title: successActionString,
          });
        },
        onError: () => {
          toast({
            status: 'error',
            title: errorActionString,
          });

          setIsAppAreaActive(!pendingAppAreaState);
        },
      },
    );

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t('pages.application_history.application_history_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex alignItems='center' gap={4}>
          {isLoadingAppArea ? (
            <>
              <Skeleton w='130px' h='21px' />
              <Skeleton w='102px' h='21px' />
            </>
          ) : (
            <>
              <Text fontWeight='bold' color='brand.500'>
                {t('pages.application_history.application_history_header')}
              </Text>

              <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                {isAppAreaActive
                  ? t('pages.application_history.application_history_enabled')
                  : t('pages.application_history.application_history_disabled')}
              </Switch>
            </>
          )}
        </Flex>

        <Flex gap='xxsmall' flexWrap='wrap'>
          {links?.map(link => (
            <Link key={link.url} to={link.url}>
              <ShortcutCard label={link.label} icon={link.icon} />
            </Link>
          ))}
        </Flex>
      </SectionBox>

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_history.application_history_title_label')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_history.application_history_description_enabed')
              : t('pages.application_history.application_history_description_disabled')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea} />
    </PageContent>
  );
};
